import {
  PimentoPost,
  PimentoPut,
  PimentoDelete,
  buildErrorMessage,
} from "./apiUtils";

export interface IKnowledgeBaseArticle {
  id: number;
  created_at: string;
  modified_at: string;
  title: string;
  slug: string;
  content: any; // TipTap JSON
  parent_id: number | null;
  position: number;
  content_plain?: string;
  tag_ids: number[];
}

export interface IKnowledgeBaseTag {
  id: number;
  created_at: string;
  modified_at: string;
  label: string;
}

class ApiKnowledgeBase {
  // Articles
  public static getArticles(
    parentId?: number | null
  ): Promise<{ articles: IKnowledgeBaseArticle[] }> {
    const url = new URL("/api/v1/fybrpedia/articles", window.location.origin);
    if (parentId !== undefined) {
      url.searchParams.set(
        "parent_id",
        parentId === null ? "null" : String(parentId)
      );
    }
    return fetch(url.toString())
      .then((res) => {
        if (!res.ok) {
          throw new Error(
            `Failed to fetch articles: ${res.status} ${res.statusText}`
          );
        }
        return res.json();
      })
      .then((response) => {
        // Backend returns { data: { articles } } format
        if (response.data && response.data.articles) {
          return { articles: response.data.articles };
        }
        // Fallback for direct { articles } format
        return response;
      });
  }

  public static getArticle(
    id: number
  ): Promise<{ article: IKnowledgeBaseArticle }> {
    return fetch(`/api/v1/fybrpedia/articles/${id}`)
      .then((res) => {
        if (!res.ok) {
          throw new Error(
            `Failed to fetch article: ${res.status} ${res.statusText}`
          );
        }
        return res.json();
      })
      .then((response) => {
        // Backend returns { data: { article } } format
        if (response.data && response.data.article) {
          return { article: response.data.article };
        }
        // Fallback for direct { article } format
        return response;
      });
  }

  public static searchArticles(
    searchTerm: string
  ): Promise<{ articles: IKnowledgeBaseArticle[] }> {
    const url = new URL(
      "/api/v1/fybrpedia/articles/search",
      window.location.origin
    );
    url.searchParams.set("q", searchTerm);
    return fetch(url.toString())
      .then((res) => {
        if (!res.ok) {
          throw new Error(
            `Failed to search articles: ${res.status} ${res.statusText}`
          );
        }
        return res.json();
      })
      .then((response) => {
        // Backend returns { data: { articles } } format
        if (response.data && response.data.articles) {
          return { articles: response.data.articles };
        }
        // Fallback for direct { articles } format
        return response;
      });
  }

  public static createArticle(data: {
    title: string;
    slug: string;
    content: any;
    parent_id?: number | null;
    position?: number;
  }): Promise<{ article: IKnowledgeBaseArticle }> {
    return PimentoPost("/api/v1/fybrpedia/articles", data, {
      error: "Failed to create article",
    }).then((response) => {
      // Backend returns { data: { article } } format
      if (response.data && response.data.article) {
        return { article: response.data.article };
      }
      // Fallback for direct { article } format
      return response;
    });
  }

  public static updateArticle(
    id: number,
    data: Partial<{
      title: string;
      slug: string;
      content: any;
      parent_id: number | null;
      position: number;
    }>
  ): Promise<{ article: IKnowledgeBaseArticle }> {
    return PimentoPut(`/api/v1/fybrpedia/articles/${id}`, data, {
      error: "Failed to update article",
    }).then((response) => {
      // Backend returns { data: { article } } format
      if (response.data && response.data.article) {
        return { article: response.data.article };
      }
      // Fallback for direct { article } format
      return response;
    });
  }

  public static updateArticlesBatch(
    updates: Array<{ id: number; parent_id: number | null; position: number }>
  ): Promise<{ articles: IKnowledgeBaseArticle[] }> {
    return PimentoPost(
      "/api/v1/fybrpedia/articles/batch",
      { updates },
      {
        error: "Failed to update article positions",
      }
    ).then((response) => {
      // Backend returns { data: { articles } } format
      if (response.data && response.data.articles) {
        return { articles: response.data.articles };
      }
      // Fallback for direct { articles } format
      return response;
    });
  }

  public static deleteArticle(id: number): Promise<{ message: string }> {
    return PimentoDelete(`/api/v1/fybrpedia/articles/${id}`, {
      error: "Failed to delete article",
    }).then((response) => {
      // Backend returns { data: { message } } or { message } format
      if (response.data && response.data.message) {
        return { message: response.data.message };
      }
      if (response.message) {
        return { message: response.message };
      }
      return { message: "Article deleted" };
    });
  }

  // Tags
  public static getTags(): Promise<{ tags: IKnowledgeBaseTag[] }> {
    return fetch("/api/v1/fybrpedia/tags")
      .then((res) => {
        if (!res.ok) {
          throw new Error(
            `Failed to fetch tags: ${res.status} ${res.statusText}`
          );
        }
        return res.json();
      })
      .then((response) => {
        // Backend returns { data: { tags } } format
        if (response.data && response.data.tags) {
          return { tags: response.data.tags };
        }
        // Fallback for direct { tags } format
        return response;
      });
  }

  public static createTag(label: string): Promise<{ tag: IKnowledgeBaseTag }> {
    return PimentoPost(
      "/api/v1/fybrpedia/tags",
      { label },
      {
        error: "Failed to create tag",
      }
    ).then((response) => {
      // Backend returns { data: { tag } } format
      if (response.data && response.data.tag) {
        return { tag: response.data.tag };
      }
      // Fallback for direct { tag } format
      return response;
    });
  }

  public static assignTagToArticle(
    articleId: number,
    tagId: number
  ): Promise<{ message: string }> {
    return PimentoPost(
      `/api/v1/fybrpedia/articles/${articleId}/tags`,
      { tagId },
      {
        error: "Failed to assign tag",
      }
    ).then((response) => {
      // Backend returns { data: { message } } or { message } format
      if (response.data && response.data.message) {
        return { message: response.data.message };
      }
      if (response.message) {
        return { message: response.message };
      }
      return { message: "Tag assigned" };
    });
  }

  public static removeTagFromArticle(
    articleId: number,
    tagId: number
  ): Promise<{ message: string }> {
    return PimentoDelete(
      `/api/v1/fybrpedia/articles/${articleId}/tags/${tagId}`,
      {
        error: "Failed to remove tag",
      }
    ).then((response) => {
      // Backend returns { data: { message } } or { message } format
      if (response.data && response.data.message) {
        return { message: response.data.message };
      }
      if (response.message) {
        return { message: response.message };
      }
      return { message: "Tag removed" };
    });
  }

  public static async uploadImage(file: File): Promise<{ url: string }> {
    const requestBody = {
      filename: file.name,
      contentType: file.type,
    };

    const presignRes = await fetch("/api/v1/fybrpedia/images/presign", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(requestBody),
    });

    if (!presignRes.ok) {
      throw new Error(
        await buildErrorMessage(presignRes, "Failed to get presigned url")
      );
    }

    const presignData = await presignRes.json();
    const uploadUrl = presignData.data?.uploadUrl || presignData.uploadUrl;
    const publicUrl = presignData.data?.url || presignData.url;

    if (!uploadUrl || !publicUrl) {
      throw new Error("Presign response missing uploadUrl or url");
    }

    const putRes = await fetch(uploadUrl, {
      method: "PUT",
      headers: {
        "Content-Type": file.type || "application/octet-stream",
      },
      body: file,
    });

    if (!putRes.ok) {
      throw new Error(
        await buildErrorMessage(
          putRes,
          "Failed to upload image to storage service"
        )
      );
    }

    return { url: publicUrl };
  }

  public static getImageDownloadUrl(
    key: string
  ): Promise<{ downloadUrl: string }> {
    return fetch("/api/v1/fybrpedia/images/presign-download", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ key }),
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error(
            `Failed to get presigned download url: ${res.status} ${res.statusText}`
          );
        }
        return res.json();
      })
      .then((response) => {
        const downloadUrl = response.data?.downloadUrl || response.downloadUrl;

        if (!downloadUrl) {
          throw new Error("Presign response missing downloadUrl");
        }

        return { downloadUrl };
      });
  }

  public static async uploadVideo(file: File): Promise<{ url: string }> {
    const requestBody = {
      filename: file.name,
      contentType: file.type,
    };

    const presignRes = await fetch("/api/v1/fybrpedia/videos/presign", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(requestBody),
    });

    if (!presignRes.ok) {
      throw new Error(
        await buildErrorMessage(presignRes, "Failed to get video presigned url")
      );
    }

    const presignData = await presignRes.json();
    const uploadUrl = presignData.data?.uploadUrl || presignData.uploadUrl;
    const publicUrl = presignData.data?.url || presignData.url;

    if (!uploadUrl || !publicUrl) {
      throw new Error("Video presign response missing uploadUrl or url");
    }

    const putRes = await fetch(uploadUrl, {
      method: "PUT",
      headers: {
        "Content-Type": file.type || "application/octet-stream",
      },
      body: file,
    });

    if (!putRes.ok) {
      throw new Error(
        await buildErrorMessage(
          putRes,
          "Failed to upload video to storage service"
        )
      );
    }

    return { url: publicUrl };
  }

  public static async uploadVideoWithProgress(
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<{ url: string }> {
    const requestBody = {
      filename: file.name,
      contentType: file.type,
    };

    const presignRes = await fetch("/api/v1/fybrpedia/videos/presign", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(requestBody),
    });

    if (!presignRes.ok) {
      throw new Error(
        await buildErrorMessage(presignRes, "Failed to get video presigned url")
      );
    }

    const presignData = await presignRes.json();
    const uploadUrl = presignData.data?.uploadUrl || presignData.uploadUrl;
    const publicUrl = presignData.data?.url || presignData.url;

    if (!uploadUrl || !publicUrl) {
      throw new Error("Video presign response missing uploadUrl or url");
    }

    onProgress?.(0);

    return new Promise<{ url: string }>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", uploadUrl);
      xhr.setRequestHeader(
        "Content-Type",
        file.type || "application/octet-stream"
      );

      const buildXhrErrorMessage = (fallback: string) => {
        try {
          const data = JSON.parse(xhr.responseText || "{}");
          const detail = data?.error || data?.message;
          if (detail) {
            return `${fallback}: ${detail}`;
          }
        } catch {
          // ignore parse errors
        }
        return `${fallback}: ${xhr.status} ${xhr.statusText}`;
      };

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && event.total > 0) {
          onProgress?.(event.loaded / event.total);
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          onProgress?.(1);
          resolve({ url: publicUrl });
        } else {
          reject(
            new Error(
              buildXhrErrorMessage("Failed to upload video to storage service")
            )
          );
        }
      };

      xhr.onerror = () => {
        reject(
          new Error(
            buildXhrErrorMessage("Failed to upload video to storage service")
          )
        );
      };

      xhr.onabort = () => {
        reject(new Error("Video upload was aborted"));
      };

      xhr.send(file);
    });
  }

  public static getVideoDownloadUrl(
    key: string
  ): Promise<{ downloadUrl: string }> {
    return fetch("/api/v1/fybrpedia/videos/presign-download", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ key }),
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error(
            `Failed to get video presigned download url: ${res.status} ${res.statusText}`
          );
        }
        return res.json();
      })
      .then((response) => {
        const downloadUrl = response.data?.downloadUrl || response.downloadUrl;

        if (!downloadUrl) {
          throw new Error("Video presign response missing downloadUrl");
        }

        return { downloadUrl };
      });
  }
}

export default ApiKnowledgeBase;
